/**
 * Pitch as notation means it: a letter, an accidental and an octave.
 *
 * A MIDI number is not a pitch. F sharp and G flat are the same key on a piano and two
 * different notes on a page -- they sit on different lines, they are spelled differently
 * by the key they are in, and transposing a part turns one into the other. Storing a MIDI
 * number and spelling it back on the way out loses the composer's choice and cannot be
 * recovered, so every pitch here is a `step` / `alter` / `octave` triple and MIDI is only
 * ever derived from it.
 *
 * Octaves are scientific: middle C is C4 and is MIDI 60.
 *
 * ## Written and sounding pitch
 *
 * A part for a transposing instrument stores the pitch the player reads, not the pitch the
 * audience hears, and carries a {@link Transposition} saying how far apart those are. The
 * convention is MusicXML's: **add the transposition to the written pitch to get the
 * sounding pitch**. A horn in F is `chromatic: -7`, because its written C sounds the F a
 * fifth below. Getting this backwards is silent -- the file opens, the notes look
 * plausible, and every judgement about the part's range is wrong by a fifth -- so the two
 * directions are named rather than signed at the call site: {@link toSounding} and
 * {@link toWritten}.
 */

import type { TabPosition } from "./tab";

/** The seven letters, in the order their octave numbers advance. */
export const STEPS = ["C", "D", "E", "F", "G", "A", "B"] as const;

export type Step = (typeof STEPS)[number];

/** Semitones above C for each letter. */
const STEP_SEMITONES: Readonly<Record<Step, number>> = {
  C: 0,
  D: 2,
  E: 4,
  F: 5,
  G: 7,
  A: 9,
  B: 11,
};

/**
 * Whether the accidental is drawn, and which one, when the key and the bar do not already
 * imply it. `"auto"` lets the engraver decide, which is what almost every note wants;
 * the explicit values are for a courtesy accidental or an editorial one.
 */
export type AccidentalDisplay =
  | "auto"
  | "none"
  | "natural"
  | "sharp"
  | "flat"
  | "double-sharp"
  | "double-flat";

/** One note head. Several of them on one event make a chord. */
export interface NotePitch {
  readonly step: Step;
  /** Semitones of accidental: -2 double flat, 0 natural, 2 double sharp. */
  readonly alter: number;
  readonly octave: number;
  readonly accidental: AccidentalDisplay;
  /** This head begins a tie into a later head of the same pitch; `ties.ts` says which. */
  readonly tieStart: boolean;
  /** This head ends a tie from an earlier head of the same pitch. */
  readonly tieStop: boolean;
  /**
   * The drum this head is, named in the part's kit; `null` for an ordinary pitch.
   *
   * On a drum note `step` and `octave` are still read as the place on the staff, which is
   * what lets every other layer treat it as a head like any other; `alter` and
   * `accidental` say nothing, because a drum is not sharpened. What it sounds is the
   * kit's business -- see `percussion.ts`.
   */
  readonly drum: string | null;
  /**
   * Where this note is taken on a fretted instrument; `null` when nothing says.
   *
   * It sits beside the pitch rather than instead of it: the pitch is still what sounds and
   * what a notation staff draws, and the position is what a tab staff prints. See
   * `tab.ts` for why the fret is written down rather than worked out from the pitch.
   */
  readonly tab: TabPosition | null;
}

/** How far a part's written pitch is from its sounding pitch, as MusicXML states it. */
export interface Transposition {
  /** Semitones, excluding whole octaves. */
  readonly chromatic: number;
  /** Letter steps, excluding whole octaves. */
  readonly diatonic: number;
  /** Whole octaves, added on top of both of the above. */
  readonly octaveChange: number;
}

/** A part that sounds where it is written. */
export const AT_PITCH: Transposition = { chromatic: 0, diatonic: 0, octaveChange: 0 };

/** Total semitones from written to sounding, octaves included. */
export function chromaticShift(transposition: Transposition): number {
  return transposition.chromatic + 12 * transposition.octaveChange;
}

/** Total letter steps from written to sounding, octaves included. */
export function diatonicShift(transposition: Transposition): number {
  return transposition.diatonic + 7 * transposition.octaveChange;
}

/** Whether the part sounds anywhere other than where it is written. */
export function transposes(transposition: Transposition): boolean {
  return chromaticShift(transposition) !== 0 || diatonicShift(transposition) !== 0;
}

/** MIDI number of a pitch. Middle C, written `C4`, is 60. */
export function midiOf(pitch: NotePitch): number {
  return (pitch.octave + 1) * 12 + STEP_SEMITONES[pitch.step] + pitch.alter;
}

/**
 * Position of a pitch on the endless diatonic ladder, counting letters rather than
 * semitones: C4 is 28, D4 is 29. Two pitches share a staff line exactly when this is
 * equal, which is what makes it the right currency for transposition and for spacing.
 */
export function diatonicOf(pitch: NotePitch): number {
  return pitch.octave * 7 + STEPS.indexOf(pitch.step);
}

/** A pitch from a letter, an accidental and an octave, with the accidental left to the engraver. */
export function pitchOf(step: Step, alter: number, octave: number): NotePitch {
  return {
    step,
    alter,
    octave,
    accidental: "auto",
    tieStart: false,
    tieStop: false,
    drum: null,
    tab: null,
  };
}

/** A head on a drum staff: a place, and the drum of the part's kit written there. */
export function drumHeadOf(step: Step, octave: number, drum: string): NotePitch {
  return {
    step,
    alter: 0,
    octave,
    accidental: "none",
    tieStart: false,
    tieStop: false,
    drum,
    tab: null,
  };
}

/**
 * Move a pitch by a number of letter steps and semitones, keeping its identity.
 *
 * The letter moves by `diatonic` and the accidental is then whatever makes the pitch land
 * `chromatic` semitones away -- which is what transposition means on a page, and why a
 * transposed F sharp can come back as a G flat. No enharmonic decision is taken here: the
 * interval decides the spelling.
 */
export function shiftPitch(pitch: NotePitch, diatonic: number, chromatic: number): NotePitch {
  const ladder = diatonicOf(pitch) + diatonic;
  const octave = Math.floor(ladder / 7);
  const step = STEPS[((ladder % 7) + 7) % 7] as Step;
  const natural = (octave + 1) * 12 + STEP_SEMITONES[step];
  return { ...pitch, step, octave, alter: midiOf(pitch) + chromatic - natural };
}

/** The pitch a listener hears, from the pitch the player reads. */
export function toSounding(written: NotePitch, transposition: Transposition): NotePitch {
  return shiftPitch(written, diatonicShift(transposition), chromaticShift(transposition));
}

/** The pitch the player reads, from the pitch a listener hears. */
export function toWritten(sounding: NotePitch, transposition: Transposition): NotePitch {
  return shiftPitch(sounding, -diatonicShift(transposition), -chromaticShift(transposition));
}

/** Letter names of the sharps and flats a key signature raises or lowers, in order. */
const SHARP_ORDER: readonly Step[] = ["F", "C", "G", "D", "A", "E", "B"];
const FLAT_ORDER: readonly Step[] = ["B", "E", "A", "D", "G", "C", "F"];

/**
 * The accidental a key signature of `fifths` puts on a letter, in semitones.
 *
 * Needed to know whether a note's accidental has to be drawn: a note whose alteration the
 * key already states is written plain.
 */
export function keyAlter(fifths: number, step: Step): number {
  if (fifths > 0) return SHARP_ORDER.slice(0, fifths).includes(step) ? 1 : 0;
  if (fifths < 0) return FLAT_ORDER.slice(0, -fifths).includes(step) ? -1 : 0;
  return 0;
}

/**
 * The plainest spelling of a MIDI number under a key signature: sharps for a key that has
 * sharps or none, flats for a key that has flats.
 *
 * This is legible engraving, not a musical judgement, and it never changes a pitch. It is
 * used only where a pitch arrives with no spelling of its own -- from a MIDI keyboard, or
 * from an analysis that thinks in semitones.
 */
export function spell(midi: number, fifths: number): NotePitch {
  const table = fifths < 0 ? FLAT_SPELLING : SHARP_SPELLING;
  const entry = table[((midi % 12) + 12) % 12] as readonly [Step, number];
  const [step, alter] = entry;
  return pitchOf(step, alter, Math.floor((midi - alter) / 12) - 1);
}

const SHARP_SPELLING: readonly (readonly [Step, number])[] = [
  ["C", 0],
  ["C", 1],
  ["D", 0],
  ["D", 1],
  ["E", 0],
  ["F", 0],
  ["F", 1],
  ["G", 0],
  ["G", 1],
  ["A", 0],
  ["A", 1],
  ["B", 0],
];

const FLAT_SPELLING: readonly (readonly [Step, number])[] = [
  ["C", 0],
  ["D", -1],
  ["D", 0],
  ["E", -1],
  ["E", 0],
  ["F", 0],
  ["G", -1],
  ["G", 0],
  ["A", -1],
  ["A", 0],
  ["B", -1],
  ["B", 0],
];
